/**
 * One drawing per playbook, for the landing tiles.
 *
 * Inline SVG driven by CSS variables, following `Mark.tsx` — not `<img>`, or
 * the drawings would not follow the light/dark toggle and there would be two
 * files per playbook quietly drifting apart. Nothing here is fetched: the SPA
 * makes no outbound requests and this is not the place to start.
 *
 * **Three tones and one accent, and the accent is rationed.** `--gridline` is
 * the scaffolding, `--baseline` the structure, `--text-primary` the thing being
 * examined, and `--signal` marks exactly one element per drawing — the
 * finding. Same logic as the mark's single orange dot: with two in one
 * drawing, neither is the finding. For a while the finding was only the
 * darkest ink (#124), on the argument that twelve tiles made twelve oranges;
 * the page read grey and lifeless without them, and the colour came back.
 *
 * Motion is CSS, hover-triggered, and switched off under
 * `prefers-reduced-motion` — see `.tile-art` in `index.css`. Seven autoplaying
 * illustrations would be a lot of movement for a product whose voice is
 * restrained.
 */

const GRID = "var(--gridline)";
const RULE = "var(--baseline)";
const INK = "var(--text-primary)";
//     The finding. Named for its role so a drawing reads as "this is the
//     point" and not as a colour choice — one per drawing, in the signal.
const FINDING = "var(--signal)";

type ArtProps = { className?: string };

function Frame({ children, className = "" }: { children: React.ReactNode; className?: string }) {
  return (
    <svg
      viewBox="0 0 120 72"
      className={className}
      aria-hidden="true"
      focusable="false"
      fill="none"
    >
      {children}
    </svg>
  );
}

/** cdd — market attractiveness against competitive position. The flagship. */
function CommercialDueDiligence({ className }: ArtProps) {
  return (
    <Frame className={className}>
      <path d="M18 60 H108" stroke={RULE} strokeWidth="1.5" />
      <path d="M18 60 V10" stroke={RULE} strokeWidth="1.5" />
      {[24, 38, 52].map((y) => (
        <path key={y} d={`M18 ${y} H108`} stroke={GRID} strokeWidth="1" />
      ))}
      {[46, 74, 102].map((x) => (
        <path key={x} d={`M${x} 10 V60`} stroke={GRID} strokeWidth="1" />
      ))}
      {/* Competitors, sized by share; one of them is the question. */}
      <circle cx="38" cy="48" r="5" fill={GRID} />
      <circle cx="60" cy="40" r="8" fill={RULE} />
      <circle cx="94" cy="20" r="6" fill={INK} />
      <circle className="art-pulse" cx="78" cy="30" r="4.5" fill={FINDING} />
    </Frame>
  );
}

/** company-profile — one company examined in depth. */
function CompanyProfile({ className }: ArtProps) {
  return (
    <Frame className={className}>
      <rect x="42" y="12" width="36" height="48" rx="2" stroke={INK} strokeWidth="1.5" />
      {[22, 30, 38, 46].map((y) => (
        <path key={y} d={`M48 ${y} H72`} stroke={GRID} strokeWidth="1.5" />
      ))}
      <path d="M48 54 H64" stroke={RULE} strokeWidth="1.5" />
      {/* The lens, sweeping across on hover. */}
      <g className="art-sweep">
        <circle cx="60" cy="36" r="15" stroke={RULE} strokeWidth="1.5" />
        <path d="M71 47 L82 58" stroke={RULE} strokeWidth="2" strokeLinecap="round" />
      </g>
      <circle className="art-pulse" cx="54" cy="30" r="3" fill={FINDING} />
    </Frame>
  );
}

/** benchmarking — peers side by side, on one P&L line. */
function Benchmarking({ className }: ArtProps) {
  const bars = [
    { x: 24, h: 22, fill: GRID },
    { x: 44, h: 34, fill: RULE },
    { x: 64, h: 44, fill: INK },
    { x: 84, h: 28, fill: GRID },
  ];
  return (
    <Frame className={className}>
      <path d="M14 60 H108" stroke={RULE} strokeWidth="1.5" />
      {bars.map((bar) => (
        <rect
          key={bar.x}
          className="art-grow"
          x={bar.x}
          y={60 - bar.h}
          width="14"
          height={bar.h}
          rx="1.5"
          fill={bar.fill}
        />
      ))}
      {/* The peer median: the line every bar is read against. */}
      <path d="M14 30 H108" stroke={FINDING} strokeWidth="1.5" strokeDasharray="4 3" />
    </Frame>
  );
}

/**
 * bottom-up-market-model — one company at the bottom, the market built upward.
 *
 * The mirror of MarketModel above, and deliberately: that one starts from a
 * total and divides it down a tree, this one starts from a single company and
 * widens outward to the market it is actually in. The finding sits on the
 * company because that is where this method begins, which is the whole
 * difference between the two playbooks.
 */
function BottomUpMarketModel({ className }: ArtProps) {
  return (
    <Frame className={className}>
      {/* TAM, then the addressable level, then the segment — widest on top. */}
      <rect x="8" y="9" width="80" height="12" rx="2" stroke={RULE} strokeWidth="1.5" />
      <rect x="18" y="26" width="60" height="12" rx="2" stroke={GRID} strokeWidth="1.5" />
      <rect x="28" y="43" width="40" height="12" rx="2" stroke={GRID} strokeWidth="1.5" />
      {/* Built from here up. */}
      <path d="M48 60 V55" stroke={RULE} strokeWidth="1.5" />
      <path d="M48 43 V38" stroke={RULE} strokeWidth="1.5" />
      <path d="M48 26 V21" stroke={RULE} strokeWidth="1.5" />
      <rect
        className="art-pulse"
        x="36"
        y="60"
        width="24"
        height="10"
        rx="2"
        stroke={FINDING}
        strokeWidth="1.5"
      />
    </Frame>
  );
}

/** market-model — a driver tree, TAM split into what moves it. */
function MarketModel({ className }: ArtProps) {
  return (
    <Frame className={className}>
      <rect x="8" y="30" width="26" height="14" rx="2" stroke={INK} strokeWidth="1.5" />
      <path d="M34 37 H48" stroke={RULE} strokeWidth="1.5" />
      <path d="M48 16 V58" stroke={RULE} strokeWidth="1.5" />
      {[16, 37, 58].map((y) => (
        <path key={y} d={`M48 ${y} H62`} stroke={RULE} strokeWidth="1.5" />
      ))}
      <rect x="62" y="9" width="24" height="14" rx="2" stroke={GRID} strokeWidth="1.5" />
      <rect x="62" y="30" width="24" height="14" rx="2" stroke={GRID} strokeWidth="1.5" />
      <rect
        className="art-pulse"
        x="62"
        y="51"
        width="24"
        height="14"
        rx="2"
        stroke={FINDING}
        strokeWidth="1.5"
      />
      {/* The leaf that carries the number, and the one to argue about. */}
      <path d="M86 58 H100" stroke={GRID} strokeWidth="1.5" />
      <circle cx="104" cy="58" r="3" fill={GRID} />
    </Frame>
  );
}

/** industry-scan — an industry mapped end to end. */
function IndustryScan({ className }: ArtProps) {
  const nodes = [
    { x: 18, y: 22 }, { x: 42, y: 14 }, { x: 66, y: 26 },
    { x: 90, y: 18 }, { x: 30, y: 50 }, { x: 56, y: 56 },
    { x: 84, y: 46 }, { x: 102, y: 60 },
  ];
  return (
    <Frame className={className}>
      <g stroke={GRID} strokeWidth="1">
        <path d="M18 22 L42 14 L66 26 L90 18" />
        <path d="M30 50 L56 56 L84 46 L102 60" />
        <path d="M18 22 L30 50" />
        <path d="M66 26 L56 56" />
        <path d="M90 18 L84 46" />
        <path d="M42 14 L66 26" />
      </g>
      {nodes.map((node, i) => (
        <circle
          key={i}
          className="art-fade"
          style={{ animationDelay: `${i * 60}ms` }}
          cx={node.x}
          cy={node.y}
          r={i === 2 ? 5 : 3.5}
          fill={i === 2 ? INK : RULE}
        />
      ))}
      {/* The player who turns out to matter. */}
      <circle className="art-pulse" cx="84" cy="46" r="4.5" fill={FINDING} />
    </Frame>
  );
}

/** key-questions — questions in, answers out. */
function KeyQuestions({ className }: ArtProps) {
  return (
    <Frame className={className}>
      {[16, 32, 48].map((y, i) => (
        <g key={y}>
          <rect x="10" y={y - 6} width="30" height="12" rx="6" stroke={RULE} strokeWidth="1.5" />
          <path
            className="art-draw"
            style={{ animationDelay: `${i * 120}ms` }}
            d={`M40 ${y} H72`}
            stroke={GRID}
            strokeWidth="1.5"
          />
          <rect
            x="72"
            y={y - 7}
            width="38"
            height="14"
            rx="2"
            stroke={i === 1 ? FINDING : GRID}
            strokeWidth="1.5"
          />
        </g>
      ))}
      <path d="M10 62 H110" stroke={GRID} strokeWidth="1" />
      <circle cx="25" cy="62" r="0" fill={INK} />
    </Frame>
  );
}

/** storyline — titles that carry the argument, in sequence. */
function PromptMe({ className }: ArtProps) {
  // One box you type into, and the three things that come out of it. The
  // caret is the accent: everything else on this tile exists because somebody
  // typed, which makes it the finding in the sense the module header means.
  const out = [
    { x: 74, h: 22 },
    { x: 90, h: 30 },
    { x: 106, h: 16 },
  ];
  return (
    <Frame className={className}>
      {/* the prompt box */}
      <rect x="10" y="18" width="48" height="36" rx="3" stroke={RULE} strokeWidth="1.5" />
      {[27, 34, 41].map((y, i) => (
        <path
          key={y}
          className="art-draw"
          style={{ animationDelay: `${i * 130}ms` }}
          d={`M17 ${y} H${i === 2 ? 38 : 51}`}
          stroke={GRID}
          strokeWidth="1.5"
        />
      ))}
      {/* the caret, still blinking where the sentence stopped */}
      <path
        className="art-pulse"
        d="M41 37 V45"
        stroke={FINDING}
        strokeWidth="1.5"
        strokeLinecap="round"
      />

      {/* what comes back: a note, a chart, a sheet — bars standing for files */}
      {out.map((bar, i) => (
        <rect
          key={bar.x}
          className="art-rise"
          style={{ animationDelay: `${240 + i * 110}ms` }}
          x={bar.x}
          y={54 - bar.h}
          width="10"
          height={bar.h}
          rx="1.5"
          stroke={INK}
          strokeWidth="1.5"
        />
      ))}
      <path d="M68 54 H120" stroke={RULE} strokeWidth="1.5" />
    </Frame>
  );
}

/**
 * technology-explainer — a solid seen from above, opened, and its hard part
 * shown closer (#130). The same three moves the technical illustration skill
 * makes: the thing as a reader would see it, then inside it, then the one
 * detail the principle depends on. The finding is the dot in the magnifier.
 */
function TechnologyExplainer({ className }: ArtProps) {
  return (
    <Frame className={className}>
      {/* the solid, isometric: a top and two sides */}
      <path d="M14 34 L40 22 L66 34 L40 46 Z" stroke={INK} strokeWidth="1.5" />
      <path d="M14 34 V48 L40 60 V46" stroke={RULE} strokeWidth="1.5" />
      <path d="M66 34 V48 L40 60" stroke={RULE} strokeWidth="1.5" />
      {/* its layers, where the cut opens it */}
      {[39, 43].map((y, i) => (
        <path
          key={y}
          className="art-draw"
          style={{ animationDelay: `${i * 140}ms` }}
          d={`M14 ${y} L40 ${y + 12} L66 ${y}`}
          stroke={GRID}
          strokeWidth="1.2"
        />
      ))}
      {/* the leader to the magnified detail */}
      <path d="M52 40 L80 30" stroke={GRID} strokeWidth="1" strokeDasharray="2 2" />
      <circle cx="94" cy="26" r="16" stroke={RULE} strokeWidth="1.5" />
      {[20, 26, 32].map((y) => (
        <path key={y} d={`M82 ${y} H106`} stroke={GRID} strokeWidth="1.2" />
      ))}
      <circle className="art-pulse" cx="94" cy="26" r="3.5" fill={FINDING} />
    </Frame>
  );
}

function Storyline({ className }: ArtProps) {
  return (
    <Frame className={className}>
      {[
        { y: 10, w: 66 },
        { y: 24, w: 84 },
        { y: 38, w: 52 },
        { y: 52, w: 74 },
      ].map((row, i) => (
        <g key={row.y}>
          <rect
            className="art-slide"
            style={{ animationDelay: `${i * 90}ms` }}
            x="26"
            y={row.y}
            width={row.w}
            height="4"
            rx="2"
            fill={i === 2 ? FINDING : i % 2 === 0 ? INK : RULE}
          />
          <circle cx="16" cy={row.y + 2} r="3" fill={i === 2 ? FINDING : GRID} />
        </g>
      ))}
      <path d="M16 12 V54" stroke={GRID} strokeWidth="1" />
    </Frame>
  );
}

/**
 * review-cim — the claim, and what is actually true beneath it.
 *
 * The memo on the left, its claims on the right as full-width bars, and under
 * each the length the evidence actually supports. The gap is the finding, and
 * the one that is material carries the accent — which is the exaggeration
 * audit's severity scale in a drawing.
 */
function ReviewCim({ className }: ArtProps) {
  const rows = [
    { y: 14, claim: 44, actual: 36, material: false },
    { y: 34, claim: 44, actual: 17, material: true },
    { y: 54, claim: 44, actual: 30, material: false },
  ];
  return (
    <Frame className={className}>
      {/* The memo. Lines, not words: it is the object under review. */}
      <rect x="10" y="8" width="34" height="56" rx="2" stroke={RULE} strokeWidth="1.5" />
      {[18, 26, 34, 42, 50].map((y, i) => (
        <path
          key={y}
          d={`M17 ${y} H${i % 2 ? 33 : 37}`}
          stroke={GRID}
          strokeWidth="1.5"
        />
      ))}

      {rows.map((row, i) => (
        <g key={row.y}>
          {/* what the memo claims */}
          <path
            className="art-draw"
            style={{ animationDelay: `${i * 140}ms` }}
            d={`M56 ${row.y} H${56 + row.claim}`}
            stroke={GRID}
            strokeWidth="1.5"
          />
          {/* what the evidence supports */}
          <path
            className="art-slide"
            style={{ animationDelay: `${i * 140 + 200}ms` }}
            d={`M56 ${row.y + 7} H${56 + row.actual}`}
            stroke={row.material ? FINDING : INK}
            strokeWidth="2.5"
          />
        </g>
      ))}
    </Frame>
  );
}

/**
 * vendor-due-diligence — the seller's documents, and the report read off them.
 *
 * The data room on the left, the report on the right with a mark on every
 * line it verified. The weakness a buyer will find is the one marked in the
 * accent: a VDD states it first, at the weight the buyer will give it.
 */
function VendorDueDiligence({ className }: ArtProps) {
  const rows = [19, 30, 41, 52];
  return (
    <Frame className={className}>
      {/* The data room: a stack of documents, the front one open. */}
      <path d="M18 14 V10 H46 V50 H42" stroke={GRID} strokeWidth="1.5" />
      <path d="M14 18 V14 H42 V54 H38" stroke={GRID} strokeWidth="1.5" />
      <rect x="10" y="18" width="28" height="40" rx="2" stroke={RULE} strokeWidth="1.5" />
      {[27, 34, 41, 48].map((y, i) => (
        <path key={y} d={`M16 ${y} H${i % 2 ? 28 : 32}`} stroke={GRID} strokeWidth="1.5" />
      ))}

      <path className="art-draw" d="M50 36 H58 M55 33 L58 36 L55 39" stroke={RULE} strokeWidth="1.5" />

      {/* The report, every line verified; one of them is the weakness. */}
      <rect x="64" y="8" width="46" height="56" rx="2" stroke={RULE} strokeWidth="1.5" />
      {rows.map((y, i) => (
        <g key={y}>
          <rect
            className="art-fade"
            style={{
              animationDelay: `${i * 140}ms`,
              transformBox: "fill-box",
              transformOrigin: "center",
            }}
            x="70"
            y={y - 2.5}
            width="5"
            height="5"
            rx="1"
            fill={i === 1 ? FINDING : INK}
          />
          <path d={`M80 ${y} H${i % 2 ? 98 : 103}`} stroke={GRID} strokeWidth="1.5" />
        </g>
      ))}
    </Frame>
  );
}

/**
 * By playbook key, not by directory name — `competitor_benchmarking/` is
 * `benchmarking`. Getting that wrong works for `cdd` and fails for the rest,
 * which is a mistake this codebase has already made once.
 */
/**
 * white-spot — the grid, and the one cell nobody occupies.
 *
 * Every other tile draws its method's shape. This one draws its *finding*:
 * the whole point is the overlay, and the overlay is only ever read as "which
 * cell is empty". Filled cells are positions held, the outlined one is the
 * white spot.
 */
function WhiteSpot({ className }: ArtProps) {
  const cells: [number, number][] = [];
  for (let row = 0; row < 3; row += 1) {
    for (let column = 0; column < 4; column += 1) {
      cells.push([row, column]);
    }
  }
  // Row 1, column 2 is the spot. Held out of the fill rather than drawn over
  // it, so the tile has exactly one finding like every other figure.
  const spot = [1, 2];
  return (
    <Frame className={className}>
      {cells.map(([row, column]) => {
        const open = row === spot[0] && column === spot[1];
        return (
          <rect
            key={`${row}-${column}`}
            className={open ? "art-pulse" : undefined}
            x={14 + column * 24}
            y={12 + row * 18}
            width="18"
            height="12"
            rx="2"
            fill={open ? "none" : GRID}
            stroke={open ? FINDING : GRID}
            strokeWidth="1.5"
          />
        );
      })}
    </Frame>
  );
}

export const PLAYBOOK_ART: Record<
  string,
  (props: ArtProps) => JSX.Element
> = {
  "prompt-me": PromptMe,
  cdd: CommercialDueDiligence,
  "company-profile": CompanyProfile,
  benchmarking: Benchmarking,
  "market-model": MarketModel,
  "bottom-up-market-model": BottomUpMarketModel,
  "industry-scan": IndustryScan,
  "key-questions": KeyQuestions,
  storyline: Storyline,
  "review-cim": ReviewCim,
  "vendor-due-diligence": VendorDueDiligence,
  "white-spot": WhiteSpot,
  "technology-explainer": TechnologyExplainer,
  // Shipped, and the same product as the owned one below — so the same bars.
  "actionable-sales-growth-v5": ActionableSalesGrowth,
  // Not shipped: a playbook somebody made, drawn by hand because it earned it.
  // The lookup is by key and does not care where the playbook came from.
  "actionable-sales-growth": ActionableSalesGrowth,
};

/**
 * Yours — the one drawing nobody drew by hand.
 *
 * The ten above are each about their own method: a matrix, a bridge, a ranked
 * scale. A user's playbook has no method to draw yet, so this draws the *shape*
 * every playbook shares — scope, research, draft, with the finding on the
 * deliverable, which is the thing that comes out of it.
 *
 * One generic drawing rather than a procedural one seeded from the key. The ten
 * are a deliberate piece of the brand; a generated eleventh would look like what
 * it is.
 */
function Yours({ className }: ArtProps) {
  return (
    <Frame className={className}>
      <rect x="8" y="14" width="104" height="44" rx="3" stroke={GRID} />
      {[30, 56, 82].map((x) => (
        <line key={x} x1={x} y1="14" x2={x} y2="58" stroke={GRID} />
      ))}
      <line x1="8" y1="36" x2="82" y2="36" stroke={RULE} strokeWidth="1.5" />
      <circle cx="19" cy="36" r="2.5" fill={RULE} />
      <circle cx="43" cy="36" r="2.5" fill={RULE} />
      <circle cx="69" cy="36" r="2.5" fill={RULE} />
      <rect x="88" y="24" width="16" height="24" rx="1.5" stroke={INK} />
      <line x1="92" y1="31" x2="100" y2="31" stroke={INK} />
      <line x1="92" y1="36" x2="100" y2="36" stroke={INK} />
      <line x1="92" y1="41" x2="97" y2="41" stroke={INK} />
      <circle cx="97" cy="36" r="3.5" fill={FINDING} />
    </Frame>
  );
}

/**
 * Actionable sales growth — a number that goes up.
 *
 * Its own drawing rather than the generic one, which is what it was taking
 * before: a fallback every owned playbook shares cannot say what one of them is
 * about, and this one is about the bars. The tallest is the finding and the only
 * finding, like every other tile.
 *
 * On hover they rise from the baseline one after another and hold. The resting
 * state is the finished chart — see `.art-rise` in `index.css`.
 */
function ActionableSalesGrowth({ className }: ArtProps) {
  const heights = [14, 20, 28, 37, 48];
  return (
    <Frame className={className}>
      <path d="M10 62 H110" stroke={RULE} strokeWidth="1.5" />
      {[22, 34, 46].map((y) => (
        <path key={y} d={`M10 ${y} H110`} stroke={GRID} strokeWidth="1" />
      ))}
      {heights.map((h, i) => {
        const last = i === heights.length - 1;
        return (
          <rect
            key={i}
            className="art-rise"
            style={{ "--i": i } as React.CSSProperties}
            x={16 + i * 19}
            y={62 - h}
            width="13"
            height={h}
            rx="1.5"
            fill={last ? FINDING : i === heights.length - 2 ? INK : RULE}
          />
        );
      })}
    </Frame>
  );
}

/**
 * A drawing for a playbook.
 *
 * An unknown key used to get **nothing**, which was right while the only
 * playbooks were the eight drawn above and is wrong the moment somebody can
 * make a ninth: their tile would render an empty frame. An owned playbook gets
 * `Yours`; an unknown shipped key still gets nothing, because that is a
 * packaging mistake rather than a user's playbook and a blank frame is the
 * honest signal for it.
 */
export function PlaybookArt({
  playbook,
  owned = false,
  className,
}: { playbook: string; owned?: boolean } & ArtProps) {
  const Art = PLAYBOOK_ART[playbook];
  if (Art) return <Art className={className} />;
  return owned ? <Yours className={className} /> : null;
}
